import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { TooltipModule } from 'primeng/tooltip';
import { InformacionEmpresaOcDetalle } from 'src/app/domain/model/informacion-empresa/informacion-empresa-oc';
import { InformacionEmpresaRepository } from 'src/app/domain/service/informacion-empresa/informacion-empresa-repository';
import { OficinaRepository } from 'src/app/domain/service/oficina/oficina-repository';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import {
  BusquedaCentroTrabajoDialogComponent,
  CentroTrabajoSeleccionado
} from '../common/busquedas/busqueda-centro-trabajo-dialog.component';
import {
  BusquedaTareaDialogComponent,
  TareaSeleccionada
} from '../common/busquedas/busqueda-tarea-dialog.component';
import {
  AsesorSeleccionado,
  BusquedaAsesorDialogComponent as AsesorSearchComponent
} from '../common/busquedas/busqueda-asesor-dialog.component';
import { Persona, getNombreCompleto } from 'src/app/domain/model/persona/persona';
import { PersonaRepository } from 'src/app/domain/service/persona/persona-repository';
import {
  BusquedaClasificacionDialogComponent,
  ClasificacionSeleccionada
} from '../common/busquedas/busqueda-clasificacion-dialog.component';

interface OpcionOficina {
  label: string;
  value: number;
}

@Component({
  selector: 'app-informacion-empresa-detalle-ordenado-consultoria',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ButtonModule,
    TableModule,
    InputTextModule,
    DropdownModule,
    TooltipModule,
    PaginatorModule,
    BusquedaTareaDialogComponent,
    BusquedaClasificacionDialogComponent,
    BusquedaCentroTrabajoDialogComponent,
    AsesorSearchComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  template: `
    <div class="p-3 sm:p-6 max-w-7xl mx-auto">
      <div class="mb-4 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <span class="text-sm text-gray-600 mb-2 flex items-center gap-2">
            Información Empresa >> Ordenado Consultoría >><b> Detalle Ordenado Consultoría</b>
          </span>
          <h1 class="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100">
            Detalle Ordenado Consultoría
          </h1>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Consulta detallada por tarea y clasificación
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <a [routerLink]="'/informacion-empresa'" [queryParams]="queryParamsPrincipal()">
            <button
              pButton
              type="button"
              label="Información Empresa"
              icon="pi pi-home"
              severity="secondary"
              class="p-button-outlined">
            </button>
          </a>
          <a [routerLink]="'/informacion-empresa/ordenado-consultoria'" [queryParams]="queryParamsRegreso()">
            <button
              pButton
              type="button"
              label="Regresar"
              icon="pi pi-arrow-left"
              severity="secondary"
              class="p-button-outlined">
            </button>
          </a>
        </div>
      </div>

      <div class="rounded-lg border border-indigo-200 bg-indigo-100 p-4 text-indigo-900 mb-4">
        <div class="font-semibold mb-2">Contexto de consulta</div>
        <div class="text-sm grid grid-cols-1 md:grid-cols-2 gap-2">
          <div class="rounded px-3 py-2 border border-indigo-100">
            <span class="font-medium">Cliente seleccionado:</span>
            <span class="ml-2">{{ nombreCliente() }}</span>
          </div>
          <div class="rounded px-3 py-2 border border-indigo-100">
            <span class="font-medium">Producto seleccionado:</span>
            <span class="ml-2">{{ productoSeleccionadoLabel() }}</span>
          </div>
        </div>
      </div>

      <div class="border border-gray-200 dark:border-gray-700 rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800">
        <div class="font-medium text-gray-700 dark:text-gray-200 mb-3 text-sm">Filtros</div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Tarea</label>
            <div class="flex items-center gap-2 min-w-0 ">
              <input pInputText [value]="tareaSeleccionadaLabel()" class="flex-1 min-w-0" readonly />
              <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]" pTooltip="Buscar tarea"
                (click)="mostrarBuscadorTarea = true"></button>
              @if (tareaSeleccionada()) {
                <button type="button" pButton icon="pi pi-times" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="danger" pTooltip="Limpiar tarea" (click)="limpiarTarea()"></button>
              }
            </div>
          </div>

          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Clasificación</label>
            <div class="flex items-center gap-2 min-w-0 ">
              <input pInputText [value]="clasificacionSeleccionadaLabel()" class="flex-1 min-w-0" readonly />
              <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]" pTooltip="Buscar clasificación"
                (click)="mostrarBuscadorClasificacion = true"></button>
              @if (clasificacionSeleccionada()) {
                <button type="button" pButton icon="pi pi-times" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="danger" pTooltip="Limpiar clasificación" (click)="limpiarClasificacion()"></button>
              }
            </div>
          </div>

          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Consultor</label>
            <div class="flex items-center gap-2 min-w-0 ">
              <input pInputText [value]="consultorSeleccionadoLabel()" class="flex-1 min-w-0" readonly />
              <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]" pTooltip="Buscar consultor"
                (click)="mostrarBuscadorConsultor = true"></button>
              @if (consultorSeleccionado()) {
                <button type="button" pButton icon="pi pi-times" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="danger" pTooltip="Limpiar consultor" (click)="limpiarConsultor()"></button>
              }
            </div>
          </div>

          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">
              Oficina <span class="text-red-500">*</span>
            </label>
            <p-dropdown
              [options]="oficinas()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="filtroOficina"
              [filter]="true"
              [showClear]="false"
              [style]="{ width: '100%' }"
              appendTo="body">
            </p-dropdown>
          </div>

          <div class="md:col-span-2">
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Centro de Trabajo</label>
            <div class="flex items-center gap-2 min-w-0 ">
              <input pInputText [value]="centroTrabajoSeleccionadoLabel()" class="flex-1 min-w-0" readonly />
              <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]" pTooltip="Buscar centro de trabajo"
                (click)="abrirBusquedaCentroTrabajo()"></button>
              @if (centroTrabajoSeleccionado()) {
                <button type="button" pButton icon="pi pi-times" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="danger" pTooltip="Limpiar centro de trabajo" (click)="limpiarCentroTrabajo()"></button>
              }
            </div>
          </div>
        </div>

        <div class="mt-4 flex flex-wrap justify-end sm:justify-start gap-3 w-full">
          <button pButton label="Limpiar" icon="pi pi-eraser" class="p-button-outlined" severity="secondary" (click)="limpiarFiltros()"></button>
          <button pButton label="Buscar" icon="pi pi-search" (click)="buscar()"></button>
        </div>
      </div>

      @if (mensaje()) {
        <div class="p-3 mt-4 rounded" [ngClass]="mensajeClass()">
          {{ mensaje() }}
        </div>
      }

      <div class="mt-4">
        <div class="md:hidden space-y-3">
          @if (!loading() && items().length === 0) {
            <div class="text-center p-4 text-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg">
              No se encontraron resultados para los filtros enviados.
            </div>
          }

          @for (item of items(); track $index) {
            <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900">
              <div class="grid grid-cols-1 gap-2 text-sm">
                <div><span class="text-gray-500">Tarea:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.tarea || '-' }}</span></div>
                <div><span class="text-gray-500">Clasificación:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.clasificacion || '-' }}</span></div>
                <div><span class="text-gray-500">CP Horas:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.cantidadPedidaH ?? 0 }}</span></div>
                <div><span class="text-gray-500">CP Uds:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.cantidadPedidaU ?? 0 }}</span></div>
                <div><span class="text-gray-500">Asesor:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.asesor || '-' }}</span></div>
                <div><span class="text-gray-500">Tipo Asesor:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.dsTipo || '-' }}</span></div>
                <div><span class="text-gray-500">Oficina:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.oficina || '-' }}</span></div>
                <div><span class="text-gray-500">Empresa:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.nomEmpresa || '-' }}</span></div>
                <div><span class="text-gray-500">Centro Trabajo:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.centroTrabajo || '-' }}</span></div>
                <div><span class="text-gray-500">Estado de Tarea:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.estadoTarea || '-' }}</span></div>
              </div>
            </div>
          }
        </div>

        <div class="hidden md:block">
        <p-table
          [resizableColumns]="true"
          columnResizeMode="expand"
          [value]="items()"
          [loading]="loading()"
          responsiveLayout="scroll"
          styleClass="tabla-informacion-empresa p-datatable-sm p-datatable-striped p-datatable-gridlines" [scrollable]="true" [tableStyle]="{ 'min-width': '72rem' }">
          <ng-template pTemplate="header">
            <tr>
              <th pResizableColumn><span class="grid-header-text" title="Tarea">Tarea</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Clasificación">Clasificación</span></th>
              <th pResizableColumn><span class="grid-header-text" title="CP Horas">CP Horas</span></th>
              <th pResizableColumn><span class="grid-header-text" title="CP Uds">CP Uds</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Asesor">Asesor</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Tipo Asesor">Tipo Asesor</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Oficina">Oficina</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Empresa">Empresa</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Centro Trabajo">Centro Trabajo</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Estado de Tarea">Estado de Tarea</span></th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-item>
            <tr>
              <td><span class="grid-cell-text" [attr.title]="item.tarea || '-'">{{ item.tarea || '-' }}</span></td>
              <td><span class="grid-cell-text" [attr.title]="item.clasificacion || '-'">{{ item.clasificacion || '-' }}</span></td>
              <td>{{ item.cantidadPedidaH ?? 0 }}</td>
              <td>{{ item.cantidadPedidaU ?? 0 }}</td>
              <td><span class="grid-cell-text" [attr.title]="item.asesor || '-'">{{ item.asesor || '-' }}</span></td>
              <td><span class="grid-cell-text" [attr.title]="item.dsTipo || '-'">{{ item.dsTipo || '-' }}</span></td>
              <td><span class="grid-cell-text" [attr.title]="item.oficina || '-'">{{ item.oficina || '-' }}</span></td>
              <td><span class="grid-cell-text" [attr.title]="item.nomEmpresa || '-'">{{ item.nomEmpresa || '-' }}</span></td>
              <td><span class="grid-cell-text" [attr.title]="item.centroTrabajo || '-'">{{ item.centroTrabajo || '-' }}</span></td>
              <td><span class="grid-cell-text" [attr.title]="item.estadoTarea || '-'">{{ item.estadoTarea || '-' }}</span></td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="10" class="text-center p-6 text-gray-500">
                No se encontraron resultados para los filtros enviados.
              </td>
            </tr>
          </ng-template>
        </p-table>
        </div>
      </div>

      <div class="mt-4 flex justify-end">
        <p-paginator
          [rows]="rows()"
          [first]="first()"
          [totalRecords]="totalRecords()"
          [rowsPerPageOptions]="[10, 20, 50]"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
          dropdownAppendTo="body"
          appendTo="body"
          (onPageChange)="onPageChange($event)">
        </p-paginator>
      </div>

      @if (mostrarBuscadorTarea) {
        <app-busqueda-tarea-dialog
          [titulo]="'Búsqueda de Tarea'"
          (onSelect)="onTareaSeleccionada($event)"
          (onClose)="mostrarBuscadorTarea = false">
        </app-busqueda-tarea-dialog>
      }

      @if (mostrarBuscadorClasificacion) {
        <app-busqueda-clasificacion-dialog
          (onSelect)="onClasificacionSeleccionada($event)"
          (onClose)="mostrarBuscadorClasificacion = false">
        </app-busqueda-clasificacion-dialog>
      }

      @if (mostrarBuscadorCentroTrabajo) {
        <app-busqueda-centro-trabajo-dialog
          [titulo]="'Búsqueda de Centro de Trabajo'"
          [nmconsecutivoBeneficiario]="idBeneficiario()"
          [nombreBeneficiario]="nombreCliente()"
          [cargarAlAbrir]="true"
          (onSelect)="onCentroTrabajoSeleccionado($event)"
          (onClose)="mostrarBuscadorCentroTrabajo = false">
        </app-busqueda-centro-trabajo-dialog>
      }

      @if (mostrarBuscadorConsultor) {
        <app-asesor-search
          (onSelect)="seleccionarConsultor($event)"
          (onClose)="cerrarBusquedaConsultor()" />
      }
    </div>
  `
})
export class InformacionEmpresaDetalleOrdenadoConsultoriaComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly informacionEmpresaRepository: InformacionEmpresaRepository = inject(InformacionEmpresaRepository);
  private readonly oficinaRepository: OficinaRepository = inject(OficinaRepository);
  private readonly usuarioState: UsuarioStateService = inject(UsuarioStateService);
  private readonly personaRepository = inject(PersonaRepository);

  readonly items = signal<InformacionEmpresaOcDetalle[]>([]);
  readonly loading = signal(false);
  readonly totalRecords = signal(0);
  readonly first = signal(0);
  readonly rows = signal(10);
  readonly oficinas = signal<OpcionOficina[]>([]);
  readonly mensaje = signal('');
  readonly mensajeTipo = signal<'info' | 'warning' | 'error'>('info');

  filtroOficina: number | null = null;
  private oficinaSesion: number | null = null;

  mostrarBuscadorTarea = false;
  readonly tareaSeleccionada = signal<TareaSeleccionada | null>(null);

  mostrarBuscadorClasificacion = false;
  readonly clasificacionSeleccionada = signal<ClasificacionSeleccionada | null>(null);

  mostrarBuscadorCentroTrabajo = false;
  readonly centroTrabajoSeleccionado = signal<CentroTrabajoSeleccionado | null>(null);

  mostrarBuscadorConsultor = false;
  readonly consultorSeleccionado = signal<Persona | null>(null);
  readonly consultoresResultado = signal<Persona[]>([]);
  readonly loadingConsultorBusqueda = signal(false);

  readonly nombreCliente = computed(() => this.route.snapshot.queryParamMap.get('nombreBeneficiario') ?? 'Sin cliente');
  readonly idBeneficiario = computed(() => this.toNumber(this.route.snapshot.queryParamMap.get('nmconsecutivoBeneficiario')));
  readonly idProducto = computed(() => this.toNumber(this.route.snapshot.queryParamMap.get('cdproductoSeleccionado')));
  readonly nombreProducto = computed(() => this.route.snapshot.queryParamMap.get('dsproductoSeleccionado') ?? 'Sin producto');

  readonly consultorSeleccionadoLabel = computed(() => {
    const consultor = this.consultorSeleccionado();
    if (!consultor) {
      const q = this.route.snapshot.queryParamMap.get('nombreConsultor');
      return q ?? '';
    }
    return getNombreCompleto(consultor).trim() || consultor.nombreCompleto || '';
  });

  readonly tareaSeleccionadaLabel = computed(() => {
    const tarea = this.tareaSeleccionada();
    return tarea ? `${tarea.cdservicio} - ${tarea.dsservicio}` : '';
  });

  readonly clasificacionSeleccionadaLabel = computed(() => {
    const clasificacion = this.clasificacionSeleccionada();
    return clasificacion ? `${clasificacion.cdclasificacion} - ${clasificacion.dsclasificacion}` : '';
  });

  readonly centroTrabajoSeleccionadoLabel = computed(() => {
    const centro = this.centroTrabajoSeleccionado();
    return centro ? centro.descripcionCompleta : '';
  });

  readonly productoSeleccionadoLabel = computed(() => {
    const codigo = this.idProducto();
    return codigo ? `${codigo} - ${this.nombreProducto()}` : this.nombreProducto();
  });

  readonly oficinaBaseLabel = computed(() => {
    const selected = this.oficinas().find((o) => o.value === this.filtroOficina);
    return selected?.label ?? '';
  });

  readonly queryParamsRegreso = computed(() => ({
    nmconsecutivoBeneficiario: this.idBeneficiario() ?? undefined,
    nombreBeneficiario: this.nombreCliente(),
    nmconsecutivoConsultor:
      this.toNumber(this.route.snapshot.queryParamMap.get('nmconsecutivoConsultor'))
      ?? this.consultorSeleccionado()?.nmconsecutivo
      ?? undefined,
    cdpersonaConsultor:
      this.toNumber(this.route.snapshot.queryParamMap.get('cdpersonaConsultor'))
      ?? this.consultorSeleccionado()?.cdpersona
      ?? undefined,
    tipoIdentificacionConsultor:
      this.route.snapshot.queryParamMap.get('tipoIdentificacionConsultor')
      ?? this.consultorSeleccionado()?.cdtipo_identificacion
      ?? undefined,
    numeroIdentificacionConsultor:
      this.route.snapshot.queryParamMap.get('numeroIdentificacionConsultor')
      ?? this.consultorSeleccionado()?.nmidentificacion
      ?? this.consultorSeleccionado()?.dni
      ?? undefined,
    nombreConsultor:
      this.route.snapshot.queryParamMap.get('nombreConsultor')
      ?? (this.consultorSeleccionadoLabel() || undefined)
  }));

  readonly queryParamsPrincipal = computed(() => ({
    nmconsecutivoBeneficiario: this.toNumber(this.route.snapshot.queryParamMap.get('nmconsecutivoBeneficiario')) ?? undefined,
    cdpersonaBeneficiario: this.toNumber(this.route.snapshot.queryParamMap.get('cdpersonaBeneficiario')) ?? undefined,
    tipoIdentificacionBeneficiario: this.route.snapshot.queryParamMap.get('tipoIdentificacionBeneficiario') ?? undefined,
    numeroIdentificacionBeneficiario: this.route.snapshot.queryParamMap.get('numeroIdentificacionBeneficiario') ?? undefined,
    nombreBeneficiario: this.route.snapshot.queryParamMap.get('nombreBeneficiario') ?? undefined,
    contratoBeneficiario: this.route.snapshot.queryParamMap.get('contratoBeneficiario') ?? undefined,
    cdcontratoBeneficiario: this.route.snapshot.queryParamMap.get('cdcontratoBeneficiario') ?? undefined,
    dscontratoBeneficiario: this.route.snapshot.queryParamMap.get('dscontratoBeneficiario') ?? undefined,
    nmconsecutivoConsultor: this.toNumber(this.route.snapshot.queryParamMap.get('nmconsecutivoConsultor')) ?? undefined,
    cdpersonaConsultor:
      this.toNumber(this.route.snapshot.queryParamMap.get('cdpersonaConsultor'))
      ?? this.consultorSeleccionado()?.cdpersona
      ?? undefined,
    tipoIdentificacionConsultor:
      this.route.snapshot.queryParamMap.get('tipoIdentificacionConsultor')
      ?? this.consultorSeleccionado()?.cdtipo_identificacion
      ?? undefined,
    numeroIdentificacionConsultor:
      this.route.snapshot.queryParamMap.get('numeroIdentificacionConsultor')
      ?? this.consultorSeleccionado()?.nmidentificacion
      ?? this.consultorSeleccionado()?.dni
      ?? undefined,
    nombreConsultor:
      this.route.snapshot.queryParamMap.get('nombreConsultor')
      ?? (this.consultorSeleccionadoLabel() || undefined)
  }));

  readonly mensajeClass = computed(() => {
    if (this.mensajeTipo() === 'error') {
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200';
    }
    if (this.mensajeTipo() === 'warning') {
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200';
    }
    return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
  });

  ngOnInit(): void {
    void this.inicializar();
  }

  private async inicializar(): Promise<void> {
    await this.precargarSesionYOficinas();
    this.precargarConsultorDesdeContexto();
    await this.buscar();
  }

  async buscar(): Promise<void> {
    if (!this.validarFiltros()) {
      return;
    }
    this.first.set(0);
    await this.consultar(0, this.rows());
  }

  async onPageChange(event: PaginatorState): Promise<void> {
    const first = event.first ?? this.first();
    const rows = event.rows ?? this.rows();
    this.first.set(first);
    this.rows.set(rows);

    if (!this.validarFiltros(false)) {
      return;
    }

    await this.consultar(first, rows);
  }

  onTareaSeleccionada(tarea: TareaSeleccionada): void {
    this.tareaSeleccionada.set(tarea);
    this.mostrarBuscadorTarea = false;
  }

  onClasificacionSeleccionada(clasificacion: ClasificacionSeleccionada): void {
    this.clasificacionSeleccionada.set(clasificacion);
    this.mostrarBuscadorClasificacion = false;
  }

  limpiarTarea(): void {
    this.tareaSeleccionada.set(null);
  }

  limpiarClasificacion(): void {
    this.clasificacionSeleccionada.set(null);
  }

  abrirBusquedaCentroTrabajo(): void {
    if (!this.idBeneficiario()) {
      this.mensajeTipo.set('warning');
      this.mensaje.set('Debes seleccionar un cliente para buscar centro de trabajo.');
      return;
    }
    this.mostrarBuscadorCentroTrabajo = true;
  }

  onCentroTrabajoSeleccionado(centro: CentroTrabajoSeleccionado): void {
    this.centroTrabajoSeleccionado.set(centro);
    this.mostrarBuscadorCentroTrabajo = false;
  }

  limpiarCentroTrabajo(): void {
    this.centroTrabajoSeleccionado.set(null);
  }

  seleccionarConsultor(consultor: Persona | AsesorSeleccionado): void {
    this.consultorSeleccionado.set(this.mapAsesorToPersona(consultor));
    this.mostrarBuscadorConsultor = false;
    this.consultoresResultado.set([]);
  }

  limpiarBusquedaConsultor(): void {
    this.consultoresResultado.set([]);
  }

  cerrarBusquedaConsultor(): void {
    this.mostrarBuscadorConsultor = false;
    this.consultoresResultado.set([]);
  }

  limpiarConsultor(): void {
    this.consultorSeleccionado.set(null);
  }

  private mapAsesorToPersona(consultor: Persona | AsesorSeleccionado): Persona {
    const raw = consultor as Record<string, unknown>;

    const nmconsecutivo = this.toNumberOrUndefined(
      raw['nmconsecutivoAsesor'] ?? raw['nmconsecutivo']
    );
    const cdpersona = this.toNumberOrUndefined(
      raw['cdpersona'] ?? raw['cdpersonaAsesor'] ?? nmconsecutivo
    );
    const cdtipoIdentificacion = this.toStringOrUndefined(
      raw['cdtipoIdentificacion'] ?? raw['cdtipo_identificacion']
    );
    const nmidentificacion = this.toStringOrUndefined(
      raw['nmidentificacion'] ?? raw['dni']
    );
    const dsnombre1 = this.toStringOrUndefined(raw['dsnombre1']);
    const dsnombre2 = this.toStringOrUndefined(raw['dsnombre2']);
    const dsapellido1 = this.toStringOrUndefined(raw['dsapellido1']);
    const dsapellido2 = this.toStringOrUndefined(raw['dsapellido2']);
    const dsnombreComercial = this.toStringOrUndefined(
      raw['dsnombreComercial'] ?? raw['dsnombre_comercial']
    );
    const snactivo = this.toStringOrUndefined(raw['snactivo']);
    const nombreCompleto = this.toStringOrUndefined(raw['nombreCompleto']);

    // Si llega como Persona completa ya normalizada, conservarla.
    if ('cdtipo_identificacion' in raw && 'nmidentificacion' in raw && !('nmconsecutivoAsesor' in raw)) {
      return consultor as Persona;
    }

    return {
      nmconsecutivo,
      cdpersona,
      cdtipo_identificacion: cdtipoIdentificacion,
      nmidentificacion,
      dsnombre1,
      dsnombre2,
      dsapellido1,
      dsapellido2,
      dsnombre_comercial: dsnombreComercial,
      snactivo,
      dni: nmidentificacion,
      nombreCompleto: getNombreCompleto({
        dsnombre1,
        dsnombre2,
        dsapellido1,
        dsapellido2
      }) || nombreCompleto
    };
  }

  private toNumberOrUndefined(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private toStringOrUndefined(value: unknown): string | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }
    const text = String(value).trim();
    return text.length > 0 ? text : undefined;
  }

  limpiarFiltros(): void {
    this.tareaSeleccionada.set(null);
    this.clasificacionSeleccionada.set(null);
    this.centroTrabajoSeleccionado.set(null);
    this.filtroOficina = this.oficinaSesion;
    this.precargarConsultorDesdeContexto();
    this.items.set([]);
    this.totalRecords.set(0);
    this.first.set(0);
    this.mensaje.set('');
  }

  private async precargarSesionYOficinas(): Promise<void> {
    const [usuario, oficinasData] = await Promise.all([
      this.usuarioState.cargar(),
      this.oficinaRepository.obtenerOficinas()
    ]);

    this.oficinas.set(
      oficinasData.map((oficina) => ({
        value: oficina.nmconsecutivo,
        label: oficina.dsoficina
      }))
    );

    const oficinaUsuario = usuario?.nmconsecOficina ?? null;
    const oficinaContexto = this.toNumber(this.route.snapshot.queryParamMap.get('inuoficinaSeleccionada'));
    this.oficinaSesion = oficinaUsuario;
    this.filtroOficina = oficinaContexto ?? oficinaUsuario;
  }

  private precargarConsultorDesdeContexto(): void {
    const nmconsecutivo = this.toNumber(this.route.snapshot.queryParamMap.get('nmconsecutivoConsultor'));
    const cdpersona = this.toNumber(this.route.snapshot.queryParamMap.get('cdpersonaConsultor'));
    const nombre = this.route.snapshot.queryParamMap.get('nombreConsultor') ?? '';

    if (!nmconsecutivo && !cdpersona && !nombre) {
      this.consultorSeleccionado.set(null);
      return;
    }

    this.consultorSeleccionado.set({
      nmconsecutivo: nmconsecutivo ?? undefined,
      cdpersona: cdpersona ?? undefined,
      nombreCompleto: nombre || undefined
    });
  }

  private validarFiltros(mostrarError = true): boolean {
    if (!this.filtroOficina) {
      if (mostrarError) {
        this.mensajeTipo.set('warning');
        this.mensaje.set('Oficina es obligatoria.');
      }
      return false;
    }

    if (!this.idProducto()) {
      if (mostrarError) {
        this.mensajeTipo.set('warning');
        this.mensaje.set('No se encontró contexto de producto para la consulta.');
      }
      return false;
    }

    return true;
  }

  private async consultar(first: number, rows: number): Promise<void> {
    this.loading.set(true);
    this.mensaje.set('');

    try {
      const resultado = await this.informacionEmpresaRepository.listarDetalleOrdenadoConsultoria({
        inutarea: this.tareaSeleccionada()?.nmconsecutivo,
        inuclasificacion: this.clasificacionSeleccionada()?.cdclasificacion,
        inuasesor: this.consultorSeleccionado()?.nmconsecutivo,
        inuoficina: this.filtroOficina ?? undefined,
        inucentrotrabajo: this.centroTrabajoSeleccionado()?.nmconsecutivo,
        inubeneficiario: this.idBeneficiario() ?? undefined,
        inuproducto: this.idProducto() ?? undefined,
        inudesde: first + 1,
        inuhasta: first + rows
      });

      this.items.set(resultado);
      this.totalRecords.set(resultado[0]?.totalRegistros ?? 0);
      if (resultado.length === 0) {
        this.mensajeTipo.set('info');
        this.mensaje.set('La consulta no retornó registros.');
      }
    } catch {
      this.items.set([]);
      this.totalRecords.set(0);
      this.mensajeTipo.set('error');
      this.mensaje.set('Error consultando detalle ordenado por consultoría.');
    } finally {
      this.loading.set(false);
    }
  }

  private toNumber(value: string | null): number | null {
    if (!value) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
